import React, { useEffect, useState } from 'react';
import { Input } from '../Input/Input';
import { Button } from '../Button/Button';
import { Modal } from '../Modal/Modal';
import { Select } from '../Select/Select';
import './AddReviewForm.scss';
import { leaderboardEvents } from '../../api/leaderboard-events';
import { useLoader } from '../../contexts/LoaderContext';
import { useAlert } from '../../contexts/AlertContext';
import { useActiveLeaderboardEvent } from '../../hooks/useActiveLeaderboardEvent';

interface AddReviewFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { quantity: number; office: string }) => void;
  participantId?: number;
  allowedOfficeNames?: string;
}

export const AddReviewForm: React.FC<AddReviewFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  participantId,
  allowedOfficeNames,
}) => {
  const [quantity, setQuantity] = useState<number | ''>('');
  const [office, setOffice] = useState<string>('');
  const [officeOptions, setOfficeOptions] = useState<{ value: string; label: string }[]>([]);
  const { showLoader, hideLoader } = useLoader();
  const { showAlert } = useAlert();
  const { activeEvent } = useActiveLeaderboardEvent();

  useEffect(() => {
    const loadParticipantOffices = async () => {
      try {
        if (!isOpen || !activeEvent?.id) return;
        showLoader();
        if (!participantId) {
          setOfficeOptions([]);
          return;
        }
        const list = await leaderboardEvents.getParticipantOffices(activeEvent.id, participantId);
        let options = list.map((o) => ({ value: String(o.id), label: o.name }));
        if (allowedOfficeNames) {
          const names = allowedOfficeNames
            .split(',')
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean);
          if (names.length) {
            const exact = options.filter((opt) => names.includes(opt.label.toLowerCase()));
            options =
              exact.length > 0
                ? exact
                : options.filter((opt) => names.some((n) => opt.label.toLowerCase().includes(n)));
          }
        }
        setOfficeOptions(options);
        if (options.length === 1) setOffice(options[0].value);
        else if (options.length > 0 && !options.some((o) => o.value === office)) setOffice('');
      } catch {
        setOfficeOptions([]);
      } finally {
        hideLoader();
      }
    };
    loadParticipantOffices();
  }, [isOpen, participantId, allowedOfficeNames, showLoader, hideLoader, office, activeEvent?.id]);

  const handleSubmit = async () => {
    try {
      if (!participantId) {
        showAlert('Participant is not selected', 'error');
        return;
      }
      if (!quantity || !office) {
        showAlert('Please fill in all fields', 'error');
        return;
      }
      showLoader();
      if (!activeEvent?.id) return;
      await leaderboardEvents.addParticipantReviews(activeEvent.id, participantId, {
        officeId: Number(office),
        quantityOfReviews: Number(quantity),
      });
      showAlert('Reviews successfully added', 'success');
      onSubmit({ quantity: Number(quantity), office });
      setQuantity('');
      setOffice('');
    } catch (e: any) {
      showAlert(e.message || 'Failed to add reviews', 'error');
    } finally {
      hideLoader();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="add-review-form">
        <h2 className="form-title">Add New Review(s)</h2>
        <Input
          label="Quantity of Reviews"
          placeholder="Enter the quantity"
          type="number"
          value={quantity}
          onChange={(e) => setQuantity(Number(e.target.value))}
        />
        <Select
          label="Choose Office"
          placeholder="Choose Office"
          options={officeOptions}
          value={office}
          onChange={setOffice}
        />
        <div className="form-actions">
          <Button variant="button" className="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="button" onClick={handleSubmit}>
            Add New Review(s)
          </Button>
        </div>
      </div>
    </Modal>
  );
};
